<template>
  <div
    class="session-modal fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
    role="dialog"
    aria-modal="true"
    :aria-label="session.title"
    @click.self="close"
  >
    <div class="session-modal__backdrop" @click="close"></div>

    <div
      class="session-modal__panel relative z-10 w-full sm:max-w-2xl bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col overflow-hidden"
    >
      <!-- Header (fixed) -->
      <header
        class="px-5 sm:px-6 pt-5 pb-4 border-b border-slate-200 flex items-start justify-between gap-3 shrink-0"
      >
        <div class="min-w-0">
          <div v-if="hasBadge" class="flex flex-wrap items-center gap-2 mb-2">
            <span
              v-if="session.isRegistered && session.status !== 'Cancelled'"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-extrabold uppercase tracking-wider leading-none"
            >
              <i class="fal fa-check-circle"></i>
              {{ $t('Views.OpenSessions.badgeRegistered') }}
            </span>
            <span
              v-if="session.status === 'Cancelled'"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200 text-[10px] font-extrabold uppercase tracking-wider leading-none"
            >
              <i class="fal fa-ban"></i>
              {{ $t('Views.OpenSessions.badgeCancelled') }}
            </span>
            <span
              v-else-if="session.isFull && !session.isRegistered"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200 text-[10px] font-extrabold uppercase tracking-wider leading-none"
            >
              <i class="fal fa-users-slash"></i>
              {{ $t('Views.OpenSessions.badgeFull') }}
            </span>
          </div>
          <h2 class="session-modal__title text-slate-900">
            {{ session.title }}
          </h2>
          <p
            v-if="session.sponsorName"
            class="session-modal__sponsor text-slate-500"
          >
            {{ session.sponsorName }}
            <span v-if="speakerSummary" class="text-slate-300 mx-1">•</span>
            <span v-if="speakerSummary">{{ speakerSummary }}</span>
          </p>
        </div>
        <button
          ref="closeButton"
          type="button"
          class="session-modal__close shrink-0 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full flex items-center justify-center transition-colors"
          :aria-label="$t('Views.OpenSessions.detailClose')"
          @click="close"
        >
          <i class="fal fa-times"></i>
        </button>
      </header>

      <!-- Slot / date / time / location (fixed) -->
      <div
        class="px-5 sm:px-6 py-3 border-b border-slate-100 bg-slate-50/60 shrink-0 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:text-sm text-slate-600 font-medium"
      >
        <span class="inline-flex items-center gap-1.5">
          <i class="fal fa-calendar-alt text-slate-400"></i>
          {{ formatDay(session.startAt) }}
        </span>
        <span class="inline-flex items-center gap-1.5">
          <i class="fal fa-clock text-slate-400"></i>
          {{ formatRange(session.startAt, session.endAt) }}
        </span>
        <span
          v-if="session.location"
          class="inline-flex items-center gap-1.5 min-w-0"
        >
          <i class="fal fa-map-marker-alt text-slate-400"></i>
          <span class="truncate">{{ session.location }}</span>
        </span>
      </div>

      <!-- Description — the ONLY scrollable region -->
      <div class="session-modal__body px-5 sm:px-6 py-4 overflow-y-auto">
        <h3
          class="session-modal__eyebrow text-slate-400 uppercase tracking-wider"
        >
          {{ $t('Views.OpenSessions.detailAbout') }}
        </h3>
        <p
          v-if="session.description"
          class="text-sm text-slate-700 leading-relaxed whitespace-pre-line break-words"
        >
          {{ session.description }}
        </p>
        <p v-else class="text-sm text-slate-400 italic">
          {{ $t('Views.OpenSessions.detailNoDescription') }}
        </p>
      </div>

      <!-- Capacity (fixed) -->
      <div class="px-5 sm:px-6 py-3 border-t border-slate-100 shrink-0">
        <div class="flex items-center justify-between mb-1.5">
          <span
            class="session-modal__eyebrow text-slate-400 uppercase tracking-wider"
          >
            {{ $t('Views.OpenSessions.capacityLabel') }}
          </span>
          <span class="text-xs font-bold text-slate-600">
            {{ session.registeredCount }} / {{ session.capacity }}
          </span>
        </div>
        <div
          class="h-1.5 bg-slate-100 rounded-full overflow-hidden"
          :aria-label="$t('Views.OpenSessions.capacityLabel')"
        >
          <div
            class="h-full rounded-full transition-all duration-300"
            :class="capacityBarColor"
            :style="{ width: capacityPercent + '%' }"
          ></div>
        </div>
      </div>

      <!-- Footer CTA (fixed) -->
      <footer
        class="px-5 sm:px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3 shrink-0"
      >
        <button
          type="button"
          class="min-h-[44px] px-4 py-2.5 rounded-xl text-sm font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 transition-colors"
          @click="close"
        >
          {{ $t('Views.OpenSessions.detailClose') }}
        </button>
        <button
          type="button"
          :disabled="cta.disabled"
          :aria-busy="pending"
          class="min-h-[44px] px-5 py-2.5 rounded-xl text-sm font-extrabold transition-all flex items-center justify-center gap-2 disabled:cursor-not-allowed"
          :class="ctaClasses"
          @click="onCtaClick"
        >
          <i v-if="pending" class="fal fa-spinner-third animate-spin"></i>
          <i v-else-if="session.isRegistered" class="fal fa-times-circle"></i>
          <i v-else class="fal fa-check-circle"></i>
          <span>{{ $t(cta.labelKey) }}</span>
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { formatEventDateTime } from '@/helpers/event-time';
import { openSessionCtaState, CTA_VARIANT } from '@/helpers/open-session-cta';
import { englishOnlyI18nMixin } from '@/mixins';

export default {
  name: 'OpenSessionDetailModal',
  mixins: [englishOnlyI18nMixin],
  props: {
    session: { type: Object, required: true },
    pending: { type: Boolean, default: false }
  },
  computed: {
    eventTz() {
      return this.$store.getters['CurrentEvent/getTimezone'];
    },
    eventMode() {
      return this.$store.getters['CurrentEvent/getTimezoneMode'];
    },
    hasBadge() {
      return (
        this.session.status === 'Cancelled' ||
        this.session.isRegistered ||
        this.session.isFull
      );
    },
    cta() {
      return openSessionCtaState(this.session, this.pending);
    },
    ctaClasses() {
      if (this.cta.variant === CTA_VARIANT.DISABLED) {
        return 'bg-slate-100 text-slate-400 border border-slate-200';
      }
      if (this.cta.variant === CTA_VARIANT.REGISTERED) {
        return 'bg-white text-rose-600 border border-rose-200 hover:bg-rose-50';
      }
      return 'bg-teal-600 text-white border border-teal-600 hover:bg-teal-700';
    },
    capacityPercent() {
      if (!this.session.capacity) return 0;
      return Math.min(
        100,
        Math.round((this.session.registeredCount / this.session.capacity) * 100)
      );
    },
    capacityBarColor() {
      if (this.session.status === 'Cancelled') return 'bg-slate-300';
      if (this.session.isFull) return 'bg-rose-400';
      if (this.capacityPercent >= 80) return 'bg-amber-400';
      return 'bg-teal-500';
    },
    speakerSummary() {
      const ids = this.session.speakerIds || [];
      if (!ids.length) return '';
      return this.$tc('Views.OpenSessions.speakerCount', ids.length, {
        count: ids.length
      });
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    // Stop the page behind the overlay from scrolling — same approach the ticket/booking
    // modals use before handing off to vue-js-modal.
    document.body.classList.add('overflow-hidden');
    this.$nextTick(() => {
      if (this.$refs.closeButton) this.$refs.closeButton.focus();
    });
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    document.body.classList.remove('overflow-hidden');
  },
  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') this.close();
    },
    close() {
      this.$emit('close');
    },
    onCtaClick() {
      if (this.cta.disabled) return;
      this.$emit(this.cta.action, this.session);
    },
    formatDay(iso) {
      return formatEventDateTime(iso, this.eventTz, this.eventMode, {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    },
    formatRange(startIso, endIso) {
      const s = formatEventDateTime(startIso, this.eventTz, this.eventMode, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const e = formatEventDateTime(endIso, this.eventTz, this.eventMode, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      if (!s || !e) return '';
      return `${s} – ${e}`;
    }
  }
};
</script>

<style scoped>
/* The overlay is mounted inside the page container, whose `space-y-6` puts a 1.5rem
   margin-top on every child after the first. Margin still offsets a fixed box, which
   pushed the whole overlay down and left an undimmed strip along the top of the screen.
   Zeroing it here keeps the overlay pinned to the viewport wherever it gets mounted. */
.session-modal {
  margin: 0 !important;
}

.session-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  animation: sessionModalFade 0.18s ease-out forwards;
}

.session-modal__panel {
  max-height: 92vh;
  animation: sessionModalRise 0.22s ease-out forwards;
}

/* Sized with explicit scoped rules rather than text-* utilities. Headings in this app render
   far larger than the utility asks for — the same thing happens to the card title — and I could
   not pin down which stylesheet wins; a scoped class rule (class + data attribute) beats both
   a bare element selector and a single utility class, so the size holds either way. */
.session-modal__title {
  font-size: 1.125rem;
  line-height: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0;
}

.session-modal__sponsor {
  font-size: 0.8125rem;
  line-height: 1.25rem;
  margin: 0.25rem 0 0 0;
}

.session-modal__eyebrow {
  font-size: 0.65rem;
  line-height: 1rem;
  font-weight: 800;
  margin: 0 0 0.5rem 0;
}

@media (min-width: 640px) {
  .session-modal__title {
    font-size: 1.25rem;
    line-height: 1.75rem;
  }
}

/* Only the description scrolls; the header, meta bar, capacity meter and footer stay put,
   so the Register button is always reachable no matter how long the description is. */
.session-modal__body {
  flex: 1 1 auto;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
}

.session-modal__close {
  width: 2.25rem;
  height: 2.25rem;
  min-width: 2.25rem;
}

@keyframes sessionModalFade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes sessionModalRise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
